import { Component, Element, h, State, Prop, Watch } from '@stencil/core';
import { GridApi, ColumnApi, GridOptions, Grid } from 'ag-grid-enterprise';
import './resource-timeline-calendar.css';
import TimesheetsCellRenderer from './timesheets-cell-renderer';
import AgGridSubtitleCellRenderer from './ag-grid-subtitle-cell-renderer';
import ResourceTimelineHeader from './resource-timeline-header';
import { LicenseKey } from './license/licenseKey';
import { GridReadyEvent } from 'ag-grid-community';

@Component({
  tag: 'resource-timeline-calendar',
  styleUrl: 'resource-timeline-calendar.css',
  shadow: true,
})
export class ResourceTimelineCalendar {
  @Element() el: HTMLElement;

  @Prop() rowData: any[] = [];
  @Prop() gridBarTitle: string = '';
  @Prop() subTitle: string = '';
  @Prop() startDate: string; //Must a parsable date string
  @Prop() endDate: string; //must be a parsable date string
  @Prop() dateKey: string = ''; //the field used a the transaction date in the data. must be parsable date string
  @Prop() columnDefs: Array<any> = [];
  @Prop() handleClickForward: () => void;
  @Prop() handleClickBackward: () => void;
  @Prop() handleClickToday: () => void;
  @Prop() handlePrimaryActionClick: () => void;
  @Prop() handleSecondaryActionClick: () => void;
  @Prop() secondaryActionTitle: string = '';
  @Prop() primaryActionTitle: string = '';
  @Prop() setAgGridOptions: (gridOptions) => void;
  @Prop() additionalGridOptions: object = {};
  @Prop() gridHeight: string;
  @Prop() subtitleStatus: string;
  @Prop() subtitleStatusMappingClasses: { [key: string]: string } = {};
  @Prop() showRowTotals: boolean = true;
  @Prop() disablePrimaryAction: boolean;
  @Prop() disableSecondaryAction: boolean;
  @Prop() dataLoading: boolean = false;
  @Prop() convertDatesFromUTC: boolean = false; //If dates are in utc but not in iso format (missing 'Z'), this will convert to the user's local time zone. Otherwise takes the dateTime at face value.
  @Prop() showLockedIcon: boolean = false;

  @State() gridOptions: GridOptions;
  @State() agGridApi: GridApi<any>;
  // @note As of ag-grid@v31 ColumnApi has been deprecated and all methods will move to agGridApi.
  @State() agGridColumnApi: ColumnApi;
  @State() groupExpandedToggle: boolean = true;

  //helper function to get dates in range
  getDates(startDate, stopDate) {
    let dateArray = [];
    let currentDate = new Date(startDate);
    while (currentDate <= stopDate) {
      dateArray.push(new Date(currentDate));
      currentDate.setDate(currentDate.getDate() + 1);
    }
    return dateArray;
  }

  //helper function to fill add dummy records for dates in range
  fillInEmptyDates(data, dateRange) {
    let filledRowData = [...data];
    dateRange.forEach(date => {
      //Create x new blank records for x days in date range so that the calendar always displays all available days
      filledRowData.push({ [this.dateKey]: date.toISOString().split('Z')[0] });
    });
    return filledRowData;
  }

  @Watch('dataLoading')
  showOverlay() {
    if (this.gridOptions && this.agGridApi && this.dataLoading) {
      this.agGridApi.showLoadingOverlay();
    }
  }

  @Watch('startDate')
  @Watch('endDate')
  deleteData(){
    if (this.gridOptions && this.agGridApi) {
      this.agGridApi.setRowData([]);
    }
  }

  //update client side data
  @Watch('rowData')
  updateRowData() {
    if (this.gridOptions && this.agGridApi && this.rowData && this.startDate && this.endDate) {
      let dateRange = this.getDates(new Date(this.startDate), new Date(this.endDate));
      const filledRowData = this.fillInEmptyDates(this.rowData, dateRange);

      // Update row data
      this.agGridApi.setRowData(filledRowData);

      if (this.showRowTotals) {
        const allColumns = this.agGridColumnApi.getAllGridColumns();
        const rowTotalColumns = allColumns.filter(column => column?.getColDef()?.pivotTotalColumnIds);
        this.agGridColumnApi.setColumnsPinned(
          rowTotalColumns.map(c => c.getColId()),
          'right',
        );
      }
      this.onResize();
    }
  }

  dateCompare(a, b) {
    const dateA = new Date(a);
    dateA.setHours(0, 0, 0, 0);
    const dateB = new Date(b);
    dateB.setHours(0, 0, 0, 0);
    return dateA.getTime() - dateB.getTime();
  }

  @Watch('columnDefs')
  createColumnDefs() {
    if (this.agGridApi) {
      let columnDefs = [...this.columnDefs];
      columnDefs.push({
        field: this.dateKey,
        pivot: true,
        pivotComparator: this.dateCompare,
        valueGetter: params => {
          let dateString = '';
          if (this.convertDatesFromUTC) {
            dateString = new Date(params.data[this.dateKey] + 'Z').toLocaleDateString('en-US');
          } else {
            dateString = new Date(params.data[this.dateKey]).toLocaleDateString('en-US');
          }
          return dateString;
        },
      });
      this.agGridApi.setColumnDefs(columnDefs);
    }
  }

  private onResize() {
    if (this.gridOptions && this.agGridApi) {
      this.agGridApi.sizeColumnsToFit();
    }
  }

  expandColumns() {
    if (this.gridOptions && this.agGridApi && this.agGridColumnApi) {
      this.agGridColumnApi.autoSizeAllColumns();
    }
  }

  fitColumns() {
    if (this.gridOptions && this.agGridApi) {
      this.agGridApi.sizeColumnsToFit();
    }
  }

  toggleGroupExpandAll() {
    if (this.gridOptions && this.agGridApi) {
      if (this.groupExpandedToggle) {
        this.agGridApi.expandAll();
      } else {
        this.agGridApi.collapseAll();
      }
      this.groupExpandedToggle = !this.groupExpandedToggle;
    }
  }

  loadAgGridScript() {
    return import('ag-grid-enterprise');
  }

  setAgGridLicenseKey() {
    if (window['agGrid'] && window['agGrid'].LicenseManager) {
      window['agGrid'].LicenseManager.setLicenseKey(LicenseKey);
    }
  }

  initializeGrid() {
    //initial grid options
    this.gridOptions = {
      domLayout: 'normal',
      animateRows: true,
      components: {
        udpCalendarCellRenderer: TimesheetsCellRenderer,
        udpCalendarGroupRenderer: AgGridSubtitleCellRenderer,
        udpCalendarCustomHeader: ResourceTimelineHeader,
      },
      onGridReady: (event: GridReadyEvent<any>) => {
        this.agGridColumnApi = event.columnApi;
        this.agGridApi = event.api;
        if (this.setAgGridOptions) {
          this.setAgGridOptions(this.gridOptions); //put grid options into caller state
        }
        this.createColumnDefs();
        this.updateRowData();
      },
      onRowDataUpdated: params => {
        params.api.forEachNode(node => {
          if (node.key === '' || node.key === 'undefined_undefined') {
            node.setRowHeight(0);
          }
        });
        this.agGridApi.onRowHeightChanged();
      },
      rowClassRules: {
        'hide-row': params => {
          if (params.node.key === '' || params.node.key === 'undefined_undefined') {
            return true;
          }
        },
      },
      pivotMode: true,
      removePivotHeaderRowWhenSingleValueColumn: true,
      groupAllowUnbalanced: true,
      suppressAggFuncInHeader: true,
      pivotRowTotals: this.showRowTotals ? 'after' : null,
      defaultColDef: {minWidth: 60},
      
      ...this.additionalGridOptions,
    } as GridOptions;
    const myGrid = this.el.shadowRoot.querySelector('#myGrid') as HTMLElement;
    if (!myGrid) throw new Error('myGrid element not found when trying to initialize agGrid');
    new Grid(myGrid, this.gridOptions);
  }

  componentWillLoad() {
    // Load the ag-Grid script
    this.loadAgGridScript()
      .then(() => {
        // Set the ag-Grid license key
        this.setAgGridLicenseKey();
        // Initialize the ag-Grid
        this.initializeGrid();
      })
      .catch(error => {
        console.error('Error during grid initialization:', error);
      });
  }

  render() {
    return (
      <div>
        <resource-timeline-primary-bar
          gridBarTitle={this.gridBarTitle}
          subTitle={this.subTitle}
          primaryActionClick={this.handlePrimaryActionClick}
          primaryActionTitle={this.primaryActionTitle}
          secondaryActionClick={this.handleSecondaryActionClick}
          secondaryActionTitle={this.secondaryActionTitle}
          clickBackward={this.handleClickBackward}
          clickForward={this.handleClickForward}
          clickToday={this.handleClickToday}
          customFitColumnClick={this.fitColumns.bind(this)}
          subtitleStatus={this.subtitleStatus}
          subtitleStatusMappingClasses={this.subtitleStatusMappingClasses}
          disablePrimaryAction={this.disablePrimaryAction}
          disableSecondaryAction={this.disableSecondaryAction}
          toggleGroupExpandAll={this.toggleGroupExpandAll.bind(this)}
          showLockedIcon={this.showLockedIcon}
        ></resource-timeline-primary-bar>
        <div>
          {' '}
          <ag-table gridOptions={this.gridOptions} licensekey={LicenseKey} height={this.gridHeight ?? "calc(100vh - 245px)"}></ag-table>{' '}
        </div>
        <div />
      </div>
    );
  }
}
